import type { GridMap } from "../grid/GridMap.js";
import type { Position } from "../types/grid.js";
import type { PlayerCharacter, Character, SkillName } from "../types/entity.js";
import { abilityModifier } from "../types/entity.js";
import { hasLineOfSight } from "../grid/LineOfSight.js";
import { chebyshev } from "../grid/distance.js";
import { WorldStore } from "../../store/WorldStore.js";
import { EventBus } from "../EventBus.js";

// ── Tunables ─────────────────────────────────────────────────────────────────

/**
 * Chebyshev-distance radius (in cells) within which the highest-passive-
 * Perception party member auto-rolls a check on each step. Spec open
 * question is "2–3 cells"; we land on 3 — close enough that the player
 * has to walk past the doorway, far enough that a Rogue with high passive
 * Perception can spot it before stepping over it.
 */
export const SECRET_DOOR_PASSIVE_RANGE = 3;

// ── Types ────────────────────────────────────────────────────────────────────

export interface SecretDoorRecord {
  /** Persistent identifier (used as the door_id once revealed). */
  readonly doorId: string;
  readonly position: Position;
  /** Passive-Perception DC authored on the Tiled object. */
  readonly dc: number;
}

// ── Passive Perception ───────────────────────────────────────────────────────

const PERCEPTION_SKILL: SkillName = "Perception";

/**
 * SRD passive Perception: 10 + WIS modifier + proficiency (if proficient in
 * Perception, doubled if expertise). Accepts both the MVP `PlayerCharacter`
 * stub (no proficiency sets — falls back to 10 + WIS mod) and the full
 * `Character` type produced by C-series tasks.
 */
export function passivePerception(character: PlayerCharacter | Character): number {
  const wisMod = abilityModifier(character.abilities.WIS);
  const profSets = character as Partial<
    Pick<Character, "skillProficiencies" | "expertiseSkills">
  >;
  let profBonus = 0;
  if (profSets.skillProficiencies?.has(PERCEPTION_SKILL)) {
    profBonus = character.proficiencyBonus;
    if (profSets.expertiseSkills?.has(PERCEPTION_SKILL)) {
      profBonus *= 2;
    }
  }
  return 10 + wisMod + profBonus;
}

/**
 * Returns the party member with the highest passive Perception. Ties
 * break by `instanceId` so the choice is deterministic across runs and
 * test fixtures don't rely on iteration order. Returns `null` for an
 * empty or all-dead party.
 */
export function highestPassivePerceiver(
  party: readonly (PlayerCharacter | Character)[],
): PlayerCharacter | Character | null {
  let best: PlayerCharacter | Character | null = null;
  let bestScore = -Infinity;
  for (const member of party) {
    if (member.hp <= 0) continue;
    const score = passivePerception(member);
    if (
      score > bestScore ||
      (score === bestScore && best !== null && member.instanceId < best.instanceId)
    ) {
      best = member;
      bestScore = score;
    }
  }
  return best;
}

// ── Detector ─────────────────────────────────────────────────────────────────

/**
 * Per-map secret-door detector (W12).
 *
 * Register every `secret_door` Tiled object on map load; call
 * `checkOnMove(playerPos, party)` from the InputHandler step callback.
 * Doors already revealed in `WorldStore` are pre-marked at construction
 * so re-entering a map won't re-announce previously-spotted seams.
 *
 * On a successful passive check the detector:
 *   - flips `WorldStore.markSecretDoorRevealed(doorId)`
 *   - emits `world:secret_door_revealed` for the feed and renderer
 *   - skips the door on every subsequent `checkOnMove`
 */
export class SecretDoorPerception {
  private readonly doors = new Map<string, SecretDoorRecord>();
  private readonly revealed = new Set<string>();

  constructor(private readonly grid: GridMap) {}

  /**
   * Register a secret-door object. Idempotent on `doorId`. Doors already
   * revealed in `WorldStore` for the active map are skipped automatically.
   */
  register(record: SecretDoorRecord): void {
    this.doors.set(record.doorId, record);
    if (WorldStore.isSecretDoorRevealed(record.doorId)) {
      this.revealed.add(record.doorId);
    }
  }

  /** Number of doors still hidden (for tests + UI badges). */
  hiddenCount(): number {
    let n = 0;
    for (const id of this.doors.keys()) {
      if (!this.revealed.has(id)) n += 1;
    }
    return n;
  }

  /** True when the door has been discovered this session. */
  isRevealed(doorId: string): boolean {
    return this.revealed.has(doorId);
  }

  /** Drop all door registrations (called on map teardown). */
  clear(): void {
    this.doors.clear();
    this.revealed.clear();
  }

  /**
   * Roll passive Perception for the highest-passive party member against
   * every still-hidden door within `SECRET_DOOR_PASSIVE_RANGE` cells with
   * line-of-sight. Returns the doorIds revealed by this call (usually 0
   * or 1, but multiple doors clustered near each other may all reveal at
   * once). Safe to call on every step.
   */
  checkOnMove(
    playerPos: Position,
    party: readonly (PlayerCharacter | Character)[],
  ): readonly string[] {
    if (this.doors.size === 0) return [];
    const perceiver = highestPassivePerceiver(party);
    if (!perceiver) return [];

    const passive = passivePerception(perceiver);
    const newlyRevealed: string[] = [];

    for (const door of this.doors.values()) {
      if (this.revealed.has(door.doorId)) continue;
      if (chebyshev(playerPos, door.position) > SECRET_DOOR_PASSIVE_RANGE) continue;
      if (!hasLineOfSight(this.grid, playerPos, door.position)) continue;
      if (passive < door.dc) continue;

      this.revealed.add(door.doorId);
      WorldStore.markSecretDoorRevealed(door.doorId);
      EventBus.emit("world:secret_door_revealed", {
        doorId: door.doorId,
        discovererName: perceiver.name,
        dc: door.dc,
        passive,
      });
      newlyRevealed.push(door.doorId);
    }

    return newlyRevealed;
  }
}
